
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info, RefreshCw, AlertCircle } from 'lucide-react';
import { getEnvironmentDebugInfo, environmentManager } from '@/utils/environment';
import { useIsDeveloper } from '@/hooks/useIsDeveloper';
import { toast } from 'sonner';

export const EnvironmentDebugTab: React.FC = () => {
  const { data: isDeveloper = false } = useIsDeveloper();
  const [debugInfo, setDebugInfo] = React.useState(getEnvironmentDebugInfo());

  const refreshDebugInfo = () => {
    setDebugInfo(getEnvironmentDebugInfo());
    toast.success('Environment debug info refreshed');
  };

  const getEnvironmentColor = (env: string) => {
    switch (env) {
      case 'development':
        return 'bg-blue-500';
      case 'beta':
        return 'bg-orange-500';
      case 'production':
        return 'bg-green-500';
      case 'demo':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Environment Debug Info</h3>
          <p className="text-sm text-muted-foreground">
            Debug information about the current environment and mock data settings
          </p>
        </div>
        <Button onClick={refreshDebugInfo} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            Current Environment
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Detected Environment</label>
              <div className="mt-1">
                <Badge className={getEnvironmentColor(debugInfo.detectedEnvironment)}>
                  {debugInfo.detectedEnvironment}
                </Badge>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Show Mock Data</label>
              <div className="mt-1">
                <Badge variant={debugInfo.showMockData ? "destructive" : "secondary"}>
                  {debugInfo.showMockData ? "Enabled" : "Disabled"}
                </Badge>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">URL Information</label>
            <div className="text-sm space-y-1">
              <div><strong>Hostname:</strong> {debugInfo.hostname}</div>
              <div><strong>Full URL:</strong> {debugInfo.href}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Build Environment</label>
              <div className="mt-1 text-sm">
                <div>Development: {debugInfo.isDev ? '✓' : '✗'}</div>
                <div>Production: {debugInfo.isProd ? '✓' : '✗'}</div>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">User Role</label>
              <div className="mt-1">
                <Badge variant={isDeveloper ? "default" : "secondary"}>
                  {isDeveloper ? "Developer" : "Regular User"}
                </Badge>
              </div>
            </div>
          </div>

          {debugInfo.envOverride && (
            <div>
              <label className="text-sm font-medium">Environment Override</label>
              <div className="mt-1">
                <Badge variant="outline">{debugInfo.envOverride}</Badge>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>Expected Behavior for inveniocrm.com:</strong>
          <ul className="mt-2 space-y-1 text-sm">
            <li>• Environment should be detected as "beta"</li>
            <li>• Mock data should be "Disabled" for all users</li>
            <li>• New users should see empty states, not sample data</li>
          </ul>
        </AlertDescription>
      </Alert>
    </div>
  );
};
